/**
 * Models Registry List Module
 * 
 * Displays the model registry with provider filters and enable/disable actions.
 * Respects RBAC permissions (Admin-only management).
 */

import logger from '../../core/logger.js';
import { modelsService } from '../../services/models-service.js';
import { can, isAdmin } from '../../utils/rbac-helpers.js';
import { Permission } from '../../types/rbac.js';
import { ModelConfig, ModelProvider, ModelsFilters } from '../../types/prompt-model.js';
import toast from '../../ui/toast.js';

/**
 * Models list callbacks
 */
interface ModelsListCallbacks {
  onEditModel: (modelId: string) => void;
  onCreateModel: () => void;
}

let callbacks: ModelsListCallbacks | null = null;

/**
 * Register callbacks
 */
export function registerModelsListCallbacks(cbs: ModelsListCallbacks): void {
  callbacks = cbs;
}

/**
 * Current filters
 */
let currentFilters: ModelsFilters = {};

/**
 * Render the models registry view
 */
export async function renderModelsRegistryView(container: HTMLElement): Promise<void> {
  logger.info('admin: Rendering models registry');
  
  // Clear container
  container.innerHTML = '';
  
  // Check permissions - VIEW_MODELS available to all authenticated users
  if (!can(Permission.VIEW_MODELS)) {
    renderAccessDenied(container);
    return;
  }
  
  // Create header
  const header = document.createElement('div');
  header.className = 'admin-header';
  
  const heading = document.createElement('h1');
  heading.className = 'admin-heading';
  heading.textContent = 'Model Registry';
  header.appendChild(heading);
  
  const subheading = document.createElement('p');
  subheading.className = 'admin-subheading';
  subheading.textContent = 'Manage allowlisted LLM models with parameters and cost estimates';
  header.appendChild(subheading);
  
  container.appendChild(header);
  
  // Create action bar
  const actionBar = document.createElement('div');
  actionBar.style.marginBottom = '1.5rem';
  
  const createBtn = document.createElement('button');
  createBtn.className = 'btn btn--primary';
  createBtn.textContent = '+ Add New Model';
  createBtn.addEventListener('click', () => {
    if (callbacks) callbacks.onCreateModel();
  });
  actionBar.appendChild(createBtn);
  
  // Info note about default model
  const infoNote = document.createElement('div');
  infoNote.style.marginLeft = '1rem';
  infoNote.style.padding = '0.5rem 1rem';
  infoNote.style.backgroundColor = '#eff6ff';
  infoNote.style.border = '1px solid #3b82f6';
  infoNote.style.borderRadius = '0.375rem';
  infoNote.style.fontSize = '0.875rem';
  infoNote.innerHTML = '<strong>Default:</strong> gemini-2.5-flash (recommended per ADMIN-UI-DECISIONS.md)';
  actionBar.appendChild(infoNote);
  
  container.appendChild(actionBar);
  
  // Create models list section
  const listSection = document.createElement('div');
  listSection.className = 'models-list';
  
  // Filters
  const filtersDiv = renderFilters();
  listSection.appendChild(filtersDiv);
  
  // Table container
  const tableContainer = document.createElement('div');
  tableContainer.id = 'models-table-container';
  listSection.appendChild(tableContainer);
  
  container.appendChild(listSection);
  
  // Load models
  await loadAndRenderModels(tableContainer);
}

/**
 * Render filters section
 */
function renderFilters(): HTMLElement {
  const filtersDiv = document.createElement('div');
  filtersDiv.className = 'clips-filters';
  
  // Provider filter
  const providerFilter = createFilterSelect(
    'Provider',
    'provider',
    [
      { value: '', label: 'All Providers' },
      { value: ModelProvider.Gemini, label: 'Gemini' },
      { value: ModelProvider.OpenAI, label: 'OpenAI' }
    ],
    (value) => {
      currentFilters.provider = value ? (value as ModelProvider) : undefined;
      const tableContainer = document.getElementById('models-table-container');
      if (tableContainer) loadAndRenderModels(tableContainer);
    }
  );
  filtersDiv.appendChild(providerFilter);
  
  // Enabled filter
  const enabledFilter = createFilterSelect(
    'Status',
    'enabled',
    [
      { value: '', label: 'All' },
      { value: 'true', label: 'Enabled' },
      { value: 'false', label: 'Disabled' }
    ],
    (value) => {
      if (value === '') {
        currentFilters.enabled = undefined;
      } else {
        currentFilters.enabled = value === 'true';
      }
      const tableContainer = document.getElementById('models-table-container');
      if (tableContainer) loadAndRenderModels(tableContainer);
    }
  );
  filtersDiv.appendChild(enabledFilter);
  
  return filtersDiv;
}

/**
 * Create a filter select element
 */
function createFilterSelect(
  label: string,
  name: string,
  options: Array<{ value: string; label: string }>,
  onChange: (value: string) => void
): HTMLElement {
  const group = document.createElement('div');
  group.className = 'filter-group';
  
  const labelEl = document.createElement('label');
  labelEl.className = 'filter-label';
  labelEl.textContent = label;
  labelEl.htmlFor = `filter-${name}`;
  group.appendChild(labelEl);
  
  const select = document.createElement('select');
  select.id = `filter-${name}`;
  select.className = 'filter-select';
  
  options.forEach(opt => {
    const option = document.createElement('option');
    option.value = opt.value;
    option.textContent = opt.label;
    select.appendChild(option);
  });
  
  select.addEventListener('change', () => onChange(select.value));
  
  group.appendChild(select);
  return group;
}

/**
 * Load and render models table
 */
async function loadAndRenderModels(container: HTMLElement): Promise<void> {
  try {
    // Show loading state
    container.innerHTML = '<div style="padding: 2rem; text-align: center;">Loading models...</div>';
    
    // Fetch models
    const response = await modelsService.listModels(currentFilters);
    
    // Clear container
    container.innerHTML = '';
    
    if (response.models.length === 0) {
      container.innerHTML = '<div style="padding: 2rem; text-align: center; color: #6b7280;">No models found</div>';
      return;
    }
    
    // Create table
    const table = document.createElement('table');
    table.className = 'clips-table';
    
    // Table header
    const thead = document.createElement('thead');
    thead.innerHTML = `
      <tr>
        <th>Model ID</th>
        <th>Provider</th>
        <th>Temperature</th>
        <th>Max Tokens</th>
        <th>Cost/1k Tokens</th>
        <th>Status</th>
        <th>Actions</th>
      </tr>
    `;
    table.appendChild(thead);
    
    // Table body
    const tbody = document.createElement('tbody');
    response.models.forEach(model => {
      const row = createModelRow(model);
      tbody.appendChild(row);
    });
    table.appendChild(tbody);
    
    container.appendChild(table);
    
  } catch (error) {
    logger.error('admin: Failed to load models', { error });
    container.innerHTML = '<div style="padding: 2rem; text-align: center; color: #dc2626;">Failed to load models</div>';
  }
}

/**
 * Create a table row for a model
 */
function createModelRow(model: ModelConfig): HTMLTableRowElement {
  const row = document.createElement('tr');
  
  // Highlight default model
  if (model.id === 'gemini:gemini-2.5-flash') {
    row.style.backgroundColor = '#eff6ff';
  }
  
  // Model ID
  const idCell = document.createElement('td');
  idCell.className = 'clip-title-cell';
  idCell.textContent = model.id;
  if (model.id === 'gemini:gemini-2.5-flash') {
    const badge = document.createElement('span');
    badge.style.marginLeft = '0.5rem';
    badge.style.padding = '0.125rem 0.5rem';
    badge.style.backgroundColor = '#3b82f6';
    badge.style.color = 'white';
    badge.style.borderRadius = '0.25rem';
    badge.style.fontSize = '0.75rem';
    badge.style.fontWeight = 'bold';
    badge.textContent = 'DEFAULT';
    idCell.appendChild(badge);
  }
  row.appendChild(idCell);
  
  // Provider
  const providerCell = document.createElement('td');
  providerCell.textContent = model.provider.toUpperCase();
  row.appendChild(providerCell);
  
  // Temperature
  const tempCell = document.createElement('td');
  tempCell.textContent = model.parameters.temperature?.toString() || '—';
  row.appendChild(tempCell);
  
  // Max Tokens
  const tokensCell = document.createElement('td');
  tokensCell.textContent = model.parameters.maxTokens?.toString() || '—';
  row.appendChild(tokensCell);
  
  // Cost per 1k tokens
  const costCell = document.createElement('td');
  if (model.estimated_cost_per_1k_tokens) {
    costCell.textContent = `$${model.estimated_cost_per_1k_tokens.toFixed(4)}`;
  } else {
    costCell.textContent = '—';
  }
  row.appendChild(costCell);
  
  // Status
  const statusCell = document.createElement('td');
  const badge = createStatusBadge(model.enabled);
  statusCell.appendChild(badge);
  row.appendChild(statusCell);
  
  // Actions
  const actionsCell = document.createElement('td');
  actionsCell.className = 'clip-actions';
  
  // Edit button
  const editBtn = document.createElement('button');
  editBtn.className = 'action-btn';
  editBtn.textContent = 'Edit';
  editBtn.addEventListener('click', () => {
    if (callbacks) callbacks.onEditModel(model.id);
  });
  actionsCell.appendChild(editBtn);
  
  // Enable/Disable button (Admin only)
  const userIsAdmin = isAdmin();
  
  if (userIsAdmin) {
    if (model.enabled) {
      const disableBtn = document.createElement('button');
      disableBtn.className = 'action-btn action-btn--danger';
      disableBtn.textContent = 'Disable';
      disableBtn.addEventListener('click', async () => {
        await handleDisableModel(model.id);
      });
      actionsCell.appendChild(disableBtn);
    } else {
      const enableBtn = document.createElement('button');
      enableBtn.className = 'action-btn action-btn--primary';
      enableBtn.textContent = 'Enable';
      enableBtn.addEventListener('click', async () => {
        await handleEnableModel(model.id);
      });
      actionsCell.appendChild(enableBtn);
    }
    
    // Delete button (cannot delete default model)
    if (model.id !== 'gemini:gemini-2.5-flash') {
      const deleteBtn = document.createElement('button');
      deleteBtn.className = 'action-btn action-btn--danger';
      deleteBtn.textContent = 'Delete';
      deleteBtn.style.marginLeft = '0.25rem';
      deleteBtn.addEventListener('click', async () => {
        await handleDeleteModel(model.id);
      });
      actionsCell.appendChild(deleteBtn);
    }
  }
  
  row.appendChild(actionsCell);
  
  return row;
}

/**
 * Create a status badge
 */
function createStatusBadge(enabled: boolean): HTMLElement {
  const badge = document.createElement('span');
  badge.className = 'state-badge';
  
  if (enabled) {
    badge.classList.add('state-badge--approved');
    badge.textContent = 'Enabled';
  } else {
    badge.classList.add('state-badge--deprecated');
    badge.textContent = 'Disabled';
  }
  
  return badge;
}

/**
 * Action handlers
 */
async function handleEnableModel(modelId: string): Promise<void> {
  try {
    await modelsService.enableModel(modelId);
    // Reload table
    const tableContainer = document.getElementById('models-table-container');
    if (tableContainer) await loadAndRenderModels(tableContainer);
    toast.success('Model enabled');
  } catch (error) {
    logger.error('admin: Failed to enable model', { modelId, error });
    toast.error('Failed to enable model');
  }
}

async function handleDisableModel(modelId: string): Promise<void> {
  if (!confirm('Are you sure you want to disable this model?')) {
    return;
  }
  
  try {
    await modelsService.disableModel(modelId);
    const tableContainer = document.getElementById('models-table-container');
    if (tableContainer) await loadAndRenderModels(tableContainer);
    toast.success('Model disabled');
  } catch (error) {
    logger.error('admin: Failed to disable model', { modelId, error });
    toast.error('Failed to disable model');
  }
}

async function handleDeleteModel(modelId: string): Promise<void> {
  if (!confirm('Are you sure you want to delete this model? This action cannot be undone.')) {
    return;
  }
  
  try {
    await modelsService.deleteModel(modelId);
    const tableContainer = document.getElementById('models-table-container');
    if (tableContainer) await loadAndRenderModels(tableContainer);
    toast.success('Model deleted');
  } catch (error) {
    logger.error('admin: Failed to delete model', { modelId, error });
    toast.error('Failed to delete model');
  }
}

/**
 * Render access denied message
 */
function renderAccessDenied(container: HTMLElement): void {
  container.innerHTML = `
    <div class="access-denied-container">
      <h2 class="access-denied-heading">Access Denied</h2>
      <p class="access-denied-message">You do not have permission to access the model registry. Admin access required.</p>
    </div>
  `;
}
